import { useEffect, useRef, useState } from "react";

type Props = {
  suggestions: string[];
  onSuggestionClick: (text: string) => void;
};

const GAP = 8;
const AUTO_SCROLL_DELAY = 3000;

const getItemsPerView = () => {
  if (window.innerWidth <= 768) return 1;
  if (window.innerWidth <= 1024) return 2;
  return 3;
};

export default function SuggestionCarousel({
  suggestions,
  onSuggestionClick,
}: Props) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<HTMLButtonElement>(null);

  const [itemsPerView, setItemsPerView] = useState(getItemsPerView());
  const [paused, setPaused] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // 🔹 Resize handling
  useEffect(() => {
    const onResize = () => {
      const nextItems = getItemsPerView();
      setItemsPerView(nextItems);
      setCurrentIndex(0);

      if (viewportRef.current) {
        viewportRef.current.scrollTo({ left: 0 });
      }
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // 🔹 Absolute scroll (NO drift)
  const scrollToIndex = (index: number) => {
    if (!viewportRef.current || !itemRef.current) return;

    const itemSize =
      itemRef.current.getBoundingClientRect().width + GAP;

    viewportRef.current.scrollTo({
      left: index * itemSize,
      behavior: "smooth",
    });
  };

  // 🔹 Arrow controls
  const scrollByOne = (dir: "left" | "right") => {
    setPaused(true);

    setCurrentIndex((prev) => {
      const maxIndex = suggestions.length - itemsPerView;
      const next =
        dir === "left"
          ? Math.max(0, prev - 1)
          : Math.min(maxIndex, prev + 1);

      scrollToIndex(next);
      return next;
    });

    setTimeout(() => setPaused(false), 4000);
  };

  // 🔹 Auto-scroll (index based)
  useEffect(() => {
    if (paused || suggestions.length <= itemsPerView) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const maxIndex = suggestions.length - itemsPerView;
        const next = prev >= maxIndex ? 0 : prev + 1;
        scrollToIndex(next);
        return next;
      });
    }, AUTO_SCROLL_DELAY);

    return () => clearInterval(interval);
  }, [paused, itemsPerView, suggestions.length]);

  if (!suggestions.length) return null;

  const buttonWidth = `calc((100% - ${
    (itemsPerView - 1) * GAP
  }px) / ${itemsPerView})`;

  const showArrows = suggestions.length > itemsPerView;

  return (
    <div
      style={{
        width: "100%",
        padding: 8,
        // background: "#e3f2fd",
        // border: "2px solid #1976d2",
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        gap: GAP,
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* LEFT ARROW */}
      {showArrows && (
        <button
          onClick={() => scrollByOne("left")}
          style={arrowStyle}
        >
          ←
        </button>
      )}

      {/* VIEWPORT */}
      <div
        ref={viewportRef}
        style={{
          flex: 1,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: GAP,
          }}
        >
          {suggestions.map((text, i) => (
            <button
              key={i}
              ref={i === 0 ? itemRef : null}
              onClick={() => onSuggestionClick(text)}
              style={{
                width: buttonWidth,
                minWidth: buttonWidth,
                maxWidth: buttonWidth,
                padding: "8px 12px",
                borderRadius: 20,
                border: "2px solid #1976d2",
                background: "#fff",
                color: "#1976d2",
                cursor: "pointer",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                transition: "all 0.3s ease",
              }}
            >
              {text}
            </button>
          ))}
        </div>
      </div>

      {/* RIGHT ARROW */}
      {showArrows && (
        <button
          onClick={() => scrollByOne("right")}
          style={arrowStyle}
        >
          →
        </button>
      )}
    </div>
  );
}

const arrowStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: "50%",
  border: "none",
  background: "#1976d2",
  color: "#fff",
  fontSize: 18,
  cursor: "pointer",
  flexShrink: 0,
};
